import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import GoogleMapsLoader from './GoogleMapsLoader';

export interface SelectedCity {
  city: string;
  /** Alternative locality names worth trying if a postal-code lookup for `city` fails. */
  cityAlternatives: string[];
  state: string;
  country: 'US' | 'CA';
  formatted: string;
  /** City centre coordinates, when Google returns geometry. */
  lat?: number;
  lng?: number;
}

interface CityAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onCitySelected: (city: SelectedCity) => void;
  placeholder?: string;
  className?: string;
  /** Restrict predictions to these countries. Defaults to US + CA. */
  countries?: Array<'us' | 'ca'>;
}

/**
 * City autocomplete that works reliably inside modals AND across repeated use.
 *
 * The built-in google.maps.places.Autocomplete widget appends a .pac-container
 * to <body> that goes stale when the widget is re-initialized (e.g. after
 * creating one service area and adding another) — predictions stop appearing
 * until a full reload. Like AddressAutocompleteModal, this component avoids
 * the widget entirely: it fetches predictions with AutocompleteService and
 * renders its own dropdown, so every open is fresh.
 */
function CityInput({
  value,
  onChange,
  onCitySelected,
  placeholder = 'Type a city name',
  className = '',
  countries,
}: CityAutocompleteInputProps) {
  const countryList = countries && countries.length > 0 ? countries : ['us', 'ca'];
  const countryKey = countryList.join(',');
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  // Initialize the prediction service (retry until the Places library is up).
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 20;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkAndInit = () => {
      retryCount++;
      if (window.google?.maps?.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      } else if (retryCount < maxRetries) {
        timeoutId = setTimeout(checkAndInit, 500);
      } else {
        console.error('[CityAutocompleteInput] Google Maps Places not available after retries');
      }
    };

    checkAndInit();
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, []);

  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || !input || input.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        types: ['(cities)'],
        componentRestrictions: { country: countryKey.split(',') },
        sessionToken: sessionTokenRef.current || undefined,
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
          setActiveIndex(-1);
        } else {
          setPredictions([]);
          setShowDropdown(false);
        }
      },
    );
  }, [countryKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(newValue), 250);
  };

  const handleSelectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    setShowDropdown(false);
    setPredictions([]);
    onChange(prediction.description);

    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address', 'geometry'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (place, status) => {
        // New token per selection, per Google best practices.
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.address_components) return;

        // Prefer locality; fall back to cleaned admin area / sublocality —
        // same extraction the territories modal has always used.
        let city = '';
        const cityAlternatives: string[] = [];
        let state = '';
        let country: 'US' | 'CA' = 'US';
        place.address_components.forEach((component) => {
          const types = component.types;
          if (types.includes('locality')) {
            city = component.long_name;
            cityAlternatives.push(component.long_name);
          } else if (types.includes('administrative_area_level_2') && !city) {
            const cleaned = component.long_name.replace(/^(Metro|Greater|Greater Metro)\s+/i, '');
            city = cleaned;
            cityAlternatives.push(cleaned);
          } else if (types.includes('sublocality') && !city) {
            city = component.long_name;
            cityAlternatives.push(component.long_name);
          }
          if (types.includes('administrative_area_level_1')) state = component.short_name;
          if (types.includes('country')) country = component.short_name.toLowerCase() === 'ca' ? 'CA' : 'US';
        });
        if (!city && place.formatted_address) {
          city = place.formatted_address.split(',')[0].trim().replace(/^(Metro|Greater|Greater Metro)\s+/i, '');
        }

        if (city && state) {
          const formatted = place.formatted_address || `${city}, ${state}`;
          const loc = place.geometry?.location;
          const lat = loc ? loc.lat() : undefined;
          const lng = loc ? loc.lng() : undefined;
          onChange(formatted);
          onCitySelected({ city, cityAlternatives, state, country, formatted, lat, lng });
        }
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelectPrediction(predictions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close the dropdown on outside interaction.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (inputRef.current && !inputRef.current.parentElement?.contains(target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Keep the portal dropdown glued to the input on scroll/resize.
  const updateDropdownPos = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  useEffect(() => {
    if (!showDropdown || predictions.length === 0) return;
    updateDropdownPos();
    const onScrollOrResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateDropdownPos);
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [showDropdown, predictions, updateDropdownPos]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (predictions.length > 0) setShowDropdown(true); }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />

      {showDropdown && predictions.length > 0 && dropdownPos && createPortal(
        <div
          className="bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 100400,
          }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-100 last:border-b-0 flex items-start gap-2 transition-colors ${
                index === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50 active:bg-gray-100'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectPrediction(prediction);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                handleSelectPrediction(prediction);
              }}
            >
              <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {prediction.structured_formatting.main_text}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {prediction.structured_formatting.secondary_text}
                </div>
              </div>
            </button>
          ))}
          <div className="px-3 py-1.5 text-right">
            <span className="text-[10px] text-gray-400">powered by <span className="font-medium text-gray-500">Google</span></span>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function CityAutocompleteInput(props: CityAutocompleteInputProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8';
  return (
    <GoogleMapsLoader apiKey={apiKey}>
      <CityInput {...props} />
    </GoogleMapsLoader>
  );
}
